/**
 * Größenratgeber page (sections/bb-size-guide.liquid). Loaded on that template only.
 *
 * One head circumference, one cm scale, every product answers. The value lives in
 * localStorage under "bb:headsize" (per visitor, never sent anywhere) and is mirrored to
 * <html data-bb-head> and --bb-head so CSS can place markers before this module runs
 * (inline script at the top of the section).
 *
 * Elements:
 *   [data-fit-min][data-fit-max]  get data-fit = yes | no-tight | no-loose | unset
 *   [data-bb-head-text]           gets the cm value
 *   [data-bb-i18n]                JSON with the page language's sentences ({n}, {v} placeholders)
 *   <bb-tape>                     native scroll-snap tape that sets the value
 *   <bb-lineup>                   re-sorts its list by fit with an interruptible FLIP
 */

const KEY = 'bb:headsize';
const LO = 52;
const HI = 74;
const root = document.documentElement;
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

const i18n = (() => {
  try {
    return JSON.parse(document.querySelector('[data-bb-i18n]')?.textContent || '{}');
  } catch {
    return {};
  }
})();

/** @param {string} key @param {Record<string, string | number>} vars */
function t(key, vars) {
  return (i18n[key] || '').replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''));
}

function readStored() {
  try {
    const v = parseInt(localStorage.getItem(KEY), 10);
    return v >= LO && v <= HI ? v : null;
  } catch {
    return null;
  }
}

let head = readStored();

/**
 * The tape moves in whole cm. A single stated size ("Umfang: 63,5 cm") would otherwise never
 * fit anyone, so a range narrower than 1 cm counts half a cm either side.
 */
function bounds(min, max) {
  return max - min < 1 ? [min - 0.5, max + 0.5] : [min, max];
}

function fits(min, max, v = head) {
  const [a, b] = bounds(min, max);
  return v >= a && v <= b;
}

const BBFit = {
  get: () => head,
  /** @param {number} value @param {unknown} [source] */
  set(value, source) {
    const v = Math.round(value);
    if (!(v >= LO && v <= HI) || v === head) return;
    head = v;
    try {
      localStorage.setItem(KEY, String(v));
    } catch {
      /* private mode: the value still holds for this page */
    }
    reflect();
    document.dispatchEvent(new CustomEvent('bb:headsize', { detail: { value: v, source } }));
  },
  status(min, max, v = head) {
    if (v == null || Number.isNaN(min) || Number.isNaN(max)) return 'unset';
    const [a, b] = bounds(min, max);
    if (v > b) return 'no-tight';
    if (v < a) return 'no-loose';
    return 'yes';
  },
};

function applyAll(scope = document) {
  scope.querySelectorAll('[data-fit-min]').forEach((el) => {
    el.dataset.fit = BBFit.status(parseFloat(el.dataset.fitMin), parseFloat(el.dataset.fitMax));
  });
  scope.querySelectorAll('[data-bb-head-text]').forEach((el) => {
    el.textContent = head == null ? el.dataset.bbHeadEmpty || '' : `${head} cm`;
  });
}

function reflect() {
  if (head == null) {
    delete root.dataset.bbHead;
    root.style.removeProperty('--bb-head');
  } else {
    root.dataset.bbHead = String(head);
    root.style.setProperty('--bb-head', String(head));
  }
  applyAll();
}

// Other tabs.
addEventListener('storage', (e) => {
  if (e.key !== KEY) return;
  head = readStored();
  reflect();
  document.dispatchEvent(new CustomEvent('bb:headsize', { detail: { value: head, source: 'storage' } }));
});

reflect();

/* ------------------------------------------------------------------------------------ */

class BBTape extends HTMLElement {
  #scroller;
  #unit = 0;
  #shown = null;
  #intent = false;
  #settleTimer = 0;
  #frame = 0;
  #ranges = null;

  connectedCallback() {
    this.#scroller = this.querySelector('[data-tape-scroller]');
    if (!this.#scroller) return;
    this.min = Number(this.dataset.min) || LO;
    this.max = Number(this.dataset.max) || HI;
    const json = this.querySelector('[data-tape-ranges]');
    if (json) {
      try {
        this.#ranges = JSON.parse(json.textContent);
      } catch {
        this.#ranges = null;
      }
    }

    const s = this.#scroller;
    s.addEventListener('scroll', this.#onScroll, { passive: true });
    s.addEventListener('scrollend', this.#settle);
    s.addEventListener('keydown', this.#onKey);
    s.addEventListener('click', this.#onClick);
    for (const type of ['pointerdown', 'touchstart', 'wheel']) {
      s.addEventListener(type, this.#wake, { passive: true });
    }
    s.addEventListener('pointerdown', this.#onDragStart);
    this.querySelectorAll('[data-tape-step]').forEach((b) =>
      b.addEventListener('click', () => this.#step(Number(b.dataset.tapeStep)))
    );
    document.addEventListener('bb:headsize', this.#onExternal);

    if (head != null) this.dataset.state = 'set';
    this.ro = new ResizeObserver(() => this.#measure());
    this.ro.observe(s);
    this.#measure();
  }

  disconnectedCallback() {
    document.removeEventListener('bb:headsize', this.#onExternal);
    this.ro?.disconnect();
  }

  #measure() {
    const cm = this.#scroller.querySelector('[data-cm]');
    const unit = cm ? cm.getBoundingClientRect().width : 0;
    if (!unit) return;
    const changed = unit !== this.#unit;
    this.#unit = unit;
    if (changed) this.#jumpTo(head ?? (Number(this.dataset.default) || 62));
  }

  #valueAt() {
    const v = this.min + Math.round(this.#scroller.scrollLeft / this.#unit);
    return Math.min(this.max, Math.max(this.min, v));
  }

  /** Programmatic, instant. Scroll events it causes are not the visitor's choice. */
  #jumpTo(v) {
    if (!this.#unit) return;
    // Chrome re-snaps after layout changes and fires scroll/scrollend long after a jump, so only
    // a scroll that follows a real gesture (#wake, numeral click) may commit a value.
    this.#intent = false;
    this.#scroller.scrollLeft = (v - this.min) * this.#unit;
    this.#show(v);
  }

  #wake = (e) => {
    // A vertical page scroll that passes over the tape is not a pick.
    if (e.type === 'wheel' && Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    this.#intent = true;
  };

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (this.#unit) this.#show(this.#valueAt());
    });
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(this.#settle, 140); // Safari before scrollend
  };

  #settle = () => {
    clearTimeout(this.#settleTimer);
    if (!this.#intent || !this.#unit) return;
    this.#commit(this.#valueAt());
  };

  #commit(v) {
    this.dataset.state = 'set';
    BBFit.set(v, this);
    this.#shown = null;
    this.#show(v);
  }

  #onKey = (e) => {
    const step = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 5, PageDown: -5 }[e.key];
    let v;
    if (step) v = this.#valueAt() + step;
    else if (e.key === 'Home') v = this.min;
    else if (e.key === 'End') v = this.max;
    else return;
    e.preventDefault();
    v = Math.min(this.max, Math.max(this.min, v));
    // Keyboard steps never animate: repeated input must feel immediate.
    this.#jumpTo(v);
    this.#commit(v);
  };

  #step(delta) {
    if (!this.#unit) return;
    const v = Math.min(this.max, Math.max(this.min, this.#valueAt() + delta));
    this.#jumpTo(v);
    this.#commit(v);
  }

  // A mouse cannot swipe a native scroller, so drag it by hand. Touch keeps native momentum.
  #drag = null;
  #dragged = false;

  #onDragStart = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    this.#drag = { x: e.clientX, left: this.#scroller.scrollLeft, id: e.pointerId };
    this.#dragged = false;
    this.#scroller.addEventListener('pointermove', this.#onDragMove);
    this.#scroller.addEventListener('pointerup', this.#onDragEnd);
    this.#scroller.addEventListener('pointercancel', this.#onDragEnd);
  };

  #onDragMove = (e) => {
    if (!this.#drag) return;
    const dx = e.clientX - this.#drag.x;
    if (!this.#dragged && Math.abs(dx) < 4) return;
    if (!this.#dragged) {
      this.#dragged = true;
      this.#scroller.setPointerCapture(this.#drag.id);
      this.#scroller.classList.add('is-dragging');
    }
    this.#scroller.scrollLeft = this.#drag.left - dx;
  };

  #onDragEnd = () => {
    const s = this.#scroller;
    s.removeEventListener('pointermove', this.#onDragMove);
    s.removeEventListener('pointerup', this.#onDragEnd);
    s.removeEventListener('pointercancel', this.#onDragEnd);
    this.#drag = null;
    if (!this.#dragged) return;
    s.classList.remove('is-dragging');
    s.scrollTo({
      left: (this.#valueAt() - this.min) * this.#unit,
      behavior: reducedMotion.matches ? 'instant' : 'smooth',
    });
    // The click that follows a drag is not a numeral pick.
    setTimeout(() => (this.#dragged = false), 0);
  };

  #onClick = (e) => {
    if (this.#dragged) return;
    const cm = e.target.closest('[data-cm]');
    if (!cm) return;
    const v = Number(cm.dataset.cm);
    this.#intent = true;
    this.#scroller.scrollTo({
      left: (v - this.min) * this.#unit,
      behavior: reducedMotion.matches ? 'instant' : 'smooth',
    });
  };

  #onExternal = (e) => {
    if (e.detail?.source === this || e.detail?.value == null) return;
    this.dataset.state = 'set';
    this.#jumpTo(e.detail.value);
  };

  #show(v) {
    if (v === this.#shown) return;
    this.#shown = v;
    this.querySelectorAll('[data-tape-readout]').forEach((el) => (el.textContent = String(v)));
    const s = this.#scroller;
    s.setAttribute('aria-valuenow', String(v));
    s.setAttribute('aria-valuetext', `${v} cm`);
    this.querySelectorAll('[data-cm][aria-current]').forEach((el) => el.removeAttribute('aria-current'));
    s.querySelector(`[data-cm="${v}"]`)?.setAttribute('aria-current', 'true');

    const status = this.querySelector('[data-tape-status]');
    if (!status || this.dataset.state !== 'set' || !this.#ranges) return;
    const n = this.#ranges.filter(([a, b]) => fits(a, b, v)).length;
    status.textContent = t(n === 0 ? 'count0' : n === 1 ? 'count1' : 'countN', { n, v });
  }
}

/* ------------------------------------------------------------------------------------ */

const EASE_MOVE = 'cubic-bezier(0.77, 0, 0.175, 1)';

class BBLineup extends HTMLElement {
  #list;
  #visible = false;

  connectedCallback() {
    this.#list = this.querySelector('[data-lineup-list]');
    if (!this.#list) return;
    [...this.#list.children].forEach((el, i) => (el.dataset.index = String(i)));
    this.io = new IntersectionObserver(([entry]) => (this.#visible = entry.isIntersecting));
    this.io.observe(this.#list);
    document.addEventListener('bb:headsize', this.#onHead);
    if (head != null) this.sort(false);
  }

  disconnectedCallback() {
    document.removeEventListener('bb:headsize', this.#onHead);
    this.io?.disconnect();
  }

  #onHead = () => this.sort(true);

  #rank(el) {
    const idx = Number(el.dataset.index) / 1000;
    const f = el.querySelector('[data-fit-min]');
    if (!f) return 500 + idx; // stretch beanies: after the sure fits, before the misses
    const min = parseFloat(f.dataset.fitMin);
    const max = parseFloat(f.dataset.fitMax);
    return (fits(min, max) ? 0 : 1000) + Math.abs(head - (min + max) / 2) + idx;
  }

  sort(animate) {
    if (head == null) return;
    const items = [...this.#list.children];
    const sorted = items.slice().sort((a, b) => this.#rank(a) - this.#rank(b));
    const status = this.querySelector('[data-lineup-status]');
    if (status) status.textContent = t('sorted', { v: head });
    if (sorted.every((el, i) => el === items[i])) return;

    const move = animate && this.#visible && !reducedMotion.matches;
    // Read where things are *now*, mid-flight included, so a new value retargets smoothly.
    const first = move ? new Map(items.map((el) => [el, el.getBoundingClientRect()])) : null;
    items.forEach((el) => el.getAnimations().forEach((a) => a.cancel()));
    this.#list.append(...sorted);
    if (!move) return;

    for (const el of sorted) {
      const a = first.get(el);
      const b = el.getBoundingClientRect();
      const dx = a.left - b.left;
      const dy = a.top - b.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
        duration: 340,
        easing: EASE_MOVE,
      });
    }
  }
}

if (!customElements.get('bb-tape')) customElements.define('bb-tape', BBTape);
if (!customElements.get('bb-lineup')) customElements.define('bb-lineup', BBLineup);
